import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    addPromotionSelection,
    clearFilters,
    getCheckboxState,
    getCountries,
    getPromotions,
    getPromotionState,
    getSelectedPromotions,
    PromotionState,
    searchPromotions,
    setCheckboxes,
    updateCheckboxes,
} from '@emi/shared/state/filters';
import { CheckboxTreeNode, Promotion } from '@emi/shared/models';
import { Subject, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-promotions',
    templateUrl: './filter-promotions.component.html',
    styleUrls: ['./filter-promotions.component.scss'],
})
export class FilterPromotionsComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public onApplyClicked = new EventEmitter();

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    private promotionsSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public promotionsTree: Array<CheckboxTreeNode> = [];
    public selectedPromotions: Array<Promotion> = [];
    public statePromotions: Array<Promotion> = [];
    public loading: boolean;
    public showError = false;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public errorMessage = '';
    public searchText: string | null;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    public noResultsMessage = '';
    public hasSearchedByText: boolean;

    constructor(private readonly store: Store<PromotionState>) {}

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.store.dispatch(updateCheckboxes({ checkbox: selectedCheckbox }));
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.promotionsSubscription = this.store.pipe(select(getSelectedPromotions)).subscribe((promotions) => {
            if (promotions.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: promotions.map((promotion) => {
                                return this.buildCheckboxTreeNode(promotion, true);
                            }),
                        })
                    );
                }
                this.selectedPromotions = promotions;
                this.promotionsTree = promotions.map((promotion) => {
                    return this.buildCheckboxTreeNode(promotion);
                });
            }
            this.checkForResults();
        });

        this.stateSubscription = this.store.pipe(select(getPromotionState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.searchText = state.searchText;
            this.statePromotions = state.promotions;

            if (this.selectedPromotions.length === 0) {
                this.promotionsTree = state.promotions.map((promotion) => {
                    return this.buildCheckboxTreeNode(promotion);
                });
            }
            this.eventsSubject.next(this.promotionsTree);
            this.checkForResults();
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const promotions: Array<Promotion> = this.checkedArray
                .map((promotion) => new Promotion(promotion.id, promotion.label))
                .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
            this.store.dispatch(addPromotionSelection({ promotions }));
            this.store.dispatch(getCountries());
            this.store.dispatch(clearFilters());
            this.onApplyClicked.emit();
        });

        if (this.selectedPromotions.length === 0 && this.statePromotions.length === 0) {
            this.store.dispatch(getPromotions());
        }

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedPromotions.map((country) => {
                            return this.buildCheckboxTreeNode(country, true);
                        }),
                    })
                );
                const currentSelectablePromotions = this.selectedPromotions.length > 0 ? this.selectedPromotions : this.statePromotions;
                this.promotionsTree = currentSelectablePromotions.map((promotion) => {
                    return this.buildCheckboxTreeNode(promotion, this.selectedPromotions.length > 0);
                });
                this.eventsSubject.next(this.promotionsTree);
            });
        }
    }

    public buildCheckboxTreeNode(promotion: Promotion, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            return new CheckboxTreeNode(promotion.id, promotion.name, 'checked');
        }
        const existingPromotion = this.checkedArray.find((x) => x.id === promotion.id);
        return new CheckboxTreeNode(promotion.id, promotion.name, existingPromotion ? 'checked' : 'unchecked');
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(this.selectedPromotions.length !== this.checkedArray.length && this.checkedArray.length > 0);
    }

    public searchFiltersByText(text: string): void {
        this.store.dispatch(searchPromotions({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.store.dispatch(searchPromotions({ searchText: '', delay: 0 }));
    }

    public checkForResults(): void {
        if (!this.loading && this.statePromotions.length == 0 && !this.hasSearchedByText && this.selectedPromotions.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.ZERO_SKUS;
            return;
        }
        if (!this.loading && this.statePromotions.length == 0 && this.hasSearchedByText && this.selectedPromotions.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;
            return;
        }
        this.noResultsMessage = '';
    }

    public ngOnDestroy(): void {
        if (this.searchText) {
            this.store.dispatch(searchPromotions({ searchText: '', delay: 0 }));
        }
        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.promotionsSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }
}
